import { useEffect, useMemo, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { Scissors, CheckCircle2, Users, Lock } from "lucide-react";
import ShareCardPanel from "@/components/fanzone/ShareCardPanel";

const SIDE_LABELS = { offense: "Offense", defense: "Defense", specialTeam: "Special Teams" };

export default function Roster53() {
  const [pool, setPool] = useState(null);
  const [kept, setKept] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [shareKey, setShareKey] = useState(0);
  const [consensus, setConsensus] = useState(null);

  const loadConsensus = () => fz.roster53.consensus().then(({ data }) => setConsensus(data)).catch(() => {});

  useEffect(() => {
    fz.roster53.pool().then(({ data }) => {
      setPool(data);
      if (data.my?.players?.length) {
        setKept(new Set(data.my.players));
        setSubmitted(true);
        loadConsensus();
      } else {
        setKept(new Set((data.pool || []).map((p) => p.id)));
      }
    }).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    if (!pool) return [];
    const map = {};
    (pool.pool || []).forEach((p) => { (map[p.side] = map[p.side] || []).push(p); });
    return Object.entries(map).map(([k, players]) => ({
      key: k,
      label: SIDE_LABELS[k] || k,
      players: players.sort((a, b) => (a.position || "").localeCompare(b.position || "") || (a.name || "").localeCompare(b.name || "")),
    }));
  }, [pool]);

  if (!pool) return <div className="titans-card p-6 text-titans-silver text-sm">Loading the 90-man roster…</div>;

  const count = kept.size;
  const delta = count - 53;

  const toggle = (id) => {
    setKept((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (count !== 53 || busy) return;
    setBusy(true);
    try {
      const { data } = await fz.roster53.submit([...kept]);
      toast.success("Your 53-man roster is locked in!");
      if (data.award) toastAward(data.award);
      setSubmitted(true);
      setShareKey((k) => k + 1);
      loadConsensus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save roster");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fz-roster53">
      <div className="titans-card p-5 md:p-6 sticky top-16 z-20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Scissors size={16} className="text-titans-red" />
            <span className="font-display text-xl uppercase">Cut Day — Build Your 53</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={`font-display text-3xl leading-none tabular-nums ${count === 53 ? "text-emerald-600" : "text-titans-red"}`} data-testid="fz-r53-count">
                {count}<span className="text-titans-silver text-lg">/53</span>
              </div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                {delta > 0 ? `Cut ${delta} more` : delta < 0 ? `Bring ${-delta} back` : "Roster legal"}
              </div>
            </div>
            <button
              onClick={submit}
              disabled={count !== 53 || busy}
              data-testid="fz-r53-submit"
              className="bg-titans-red hover:bg-titans-red-hover text-white px-6 py-3 font-cond uppercase tracking-wider text-xs disabled:opacity-40"
            >
              {submitted ? "Update Roster" : "Lock In 53"}
            </button>
          </div>
        </div>
        <p className="text-titans-silver text-xs font-body mt-2">
          Everyone starts on the {pool.size}-man squad. Tap a player to cut him — get to exactly 53 and lock it in.
        </p>
      </div>

      {groups.map((g) => (
        <div key={g.key} className="titans-card p-5 md:p-6" data-testid={`fz-r53-group-${g.key}`}>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue mb-3 flex items-center gap-1.5">
            <Users size={11} /> {g.label} · {g.players.filter((p) => kept.has(p.id)).length} kept
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {g.players.map((p) => {
              const isKept = kept.has(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  data-testid={`fz-r53-player-${p.id}`}
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-all duration-150 ${
                    isKept ? "bg-titans-navy border-titans-navy text-white" : "border-[#E2E8F0] bg-[#F8FAFC] opacity-60"
                  }`}
                >
                  <span className={`font-display text-xs w-8 shrink-0 text-center rounded px-1 ${isKept ? "bg-white/15" : "bg-[#E2E8F0] text-titans-muted"}`}>
                    {p.position}
                  </span>
                  <span className={`font-cond uppercase tracking-wide text-xs flex-1 truncate ${isKept ? "" : "line-through"}`}>
                    {p.name}
                  </span>
                  {p.jersey && <span className={`font-cond text-[10px] shrink-0 ${isKept ? "text-white/60" : "text-titans-muted"}`}>#{p.jersey}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {submitted && (
        <ShareCardPanel
          kind="roster53"
          refreshKey={shareKey}
          text="I just made my cut-day calls — here's my Titans 53-man roster. Build yours and compare. #TitanUp #Titans"
        />
      )}

      {consensus && consensus.total > 0 && (
        <div className="titans-card p-5 md:p-6" data-testid="fz-r53-consensus">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={15} className="text-titans-blue" />
            <span className="font-display text-xl uppercase">Fanbase Consensus</span>
            <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver ml-auto">{consensus.total} GM{consensus.total === 1 ? "" : "s"} submitted</span>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5">
            {consensus.players.slice(0, 10).map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                <span className="font-cond uppercase tracking-wide text-xs flex-1 truncate">{p.name}</span>
                <span className="font-cond text-[10px] text-titans-silver">{p.position}</span>
                <div className="w-20 h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden shrink-0">
                  <div className="h-full bg-titans-blue" style={{ width: `${p.pct}%` }} />
                </div>
                <span className="font-display text-sm text-titans-blue w-10 text-right">{p.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
